import type { MultiSelectChangeEvent } from "@tql/ui-components-react/kendo/dropdowns";
import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { MultiSelect } from "@tql/ui-components-react/tql/dropdowns";
import React, { useRef, useState } from "react";

type MultiSelectCellProps = Omit<GridCustomCellProps, "onChange"> & {
	data?: unknown[];
	dataItemKey?: string;
	textField?: string;
	tagLimit?: number;
	onClose?: (value: { id: number; description: string }[]) => void;
};

const handleClick = (e: React.MouseEvent) => {
	e.stopPropagation();
};

export const MultiSelectCell = ({
	data = [],
	dataItemKey = "id",
	textField = "name",
	tagLimit = 1,
	onClose,
	...gridProps
}: MultiSelectCellProps) => {
	const dataItem = gridProps.dataItem as Record<string, unknown> | undefined;
	const field = gridProps.field || "";
	const value = (dataItem?.[field] as unknown[]) || [];
	const [state, setState] = useState(value);
	const [opened, setOpened] = useState(false);
	const openedRef = useRef(false);
	const stateRef = useRef(state);
	// Baseline for change detection: the last value actually committed via onClose.
	const lastCommittedValueRef = useRef(state);

	const commitClose = () => {
		if (
			JSON.stringify(stateRef.current) ===
			JSON.stringify(lastCommittedValueRef.current)
		) {
			return;
		}
		lastCommittedValueRef.current = stateRef.current;
		onClose?.(stateRef.current as { id: number; description: string }[]);
	};

	const handleChange = (e: MultiSelectChangeEvent) => {
		const selectedItems = e.value as unknown[];
		const isSelectAllSelected = selectedItems.some(
			(item: unknown) =>
				(item as Record<string, unknown>)?.[dataItemKey] === -1 ||
				(item as Record<string, unknown>)?.[textField] === "Select All",
		);

		const newValue = isSelectAllSelected ? [...data] : selectedItems;
		stateRef.current = newValue as { id: number; description: string }[];
		setState(newValue);

		// X buttons (tag pill X or clear-all X) fire onChange while the popup is closed.
		// Use a ref (not state) to avoid stale closure captures between Kendo events.
		if (!openedRef.current) {
			commitClose();
		}
	};

	const handleDone = (e: React.MouseEvent) => {
		e.preventDefault(); // Keep focus on MultiSelect input — prevents Kendo's focusElement() from running on a potentially stale ref
		openedRef.current = false;
		setOpened(false);
		commitClose();
	};

	return (
		<td {...gridProps.tdProps} onClick={handleClick}>
			<MultiSelect
				data={data}
				dataItemKey={dataItemKey}
				footer={
					<div style={{ padding: "4px 8px", textAlign: "right" }}>
						<Button onMouseDown={handleDone}>Done</Button>
					</div>
				}
				onChange={handleChange}
				onClose={() => {
					if (!openedRef.current) {
						return;
					}
					openedRef.current = false;
					setOpened(false);
					commitClose();
				}}
				onOpen={() => {
					openedRef.current = true;
					setOpened(true);
				}}
				opened={opened}
				selectAll={{ [dataItemKey]: -1, [textField]: "Select All" }}
				tagLimit={tagLimit}
				textField={textField}
				value={state}
			/>
		</td>
	);
};
